/**
 * Custom hook for managing GenerationAlert state and logic
 */

import { useState, useCallback } from 'react';
import { settingsStorage } from '../settings';

export interface AlertState {
    isVisible: boolean;
    type: 'missing-key' | 'success' | 'error';
    title?: string;
    message?: string;
}

export interface UseGenerationAlertReturn {
    alertState: AlertState;
    showMissingKeyAlert: () => void;
    showSuccessAlert: (message?: string) => void;
    showErrorAlert: (message?: string) => void;
    hideAlert: () => void;
    checkAuthorizationBeforeGeneration: () => boolean;
}

/**
 * Hook for managing generation alerts
 */
export function useGenerationAlert(): UseGenerationAlertReturn {
    const [alertState, setAlertState] = useState<AlertState>({
        isVisible: false,
        type: 'missing-key'
    });

    const showMissingKeyAlert = useCallback(() => {
        setAlertState({
            isVisible: true,
            type: 'missing-key',
            title: 'Authorization Required',
            message: 'You need to configure an authorization key before generating videos.'
        });
    }, []);

    const showSuccessAlert = useCallback((message?: string) => {
        setAlertState({
            isVisible: true,
            type: 'success',
            title: 'Generation Started!',
            message: message || 'Your video generation has been submitted successfully.'
        });
    }, []);

    const showErrorAlert = useCallback((message?: string) => {
        setAlertState({
            isVisible: true,
            type: 'error',
            title: 'Generation Failed',
            message: message || 'There was an error generating your video. Please try again.'
        });
    }, []);

    const hideAlert = useCallback(() => {
        setAlertState(prev => ({
            ...prev,
            isVisible: false
        }));
    }, []);

    /**
     * Check if user has authorization key before allowing generation
     * Returns true if generation can proceed, false if alert was shown
     */
    const checkAuthorizationBeforeGeneration = useCallback((): boolean => {
        const authKey = settingsStorage.getAuthorizationKey();

        if (!authKey || authKey.length === 0) {
            showMissingKeyAlert();
            return false;
        }

        return true;
    }, [showMissingKeyAlert]);

    return {
        alertState,
        showMissingKeyAlert,
        showSuccessAlert,
        showErrorAlert,
        hideAlert,
        checkAuthorizationBeforeGeneration
    };
}
